// !IMPORTANT: This file should be loaded using `type="module"`, as we load necessary plugins using `import`.
import { createZoomImageWheel } from 'https://cdn.jsdelivr.net/npm/@zoom-image/core@0.41.3/+esm'

const GalleryLightboxThumbnail = Vue.component('gallery-lightbox-thumbnail', {
  props: {
    number: Number
  },
  methods: {
    scrollTo () {
      this.$emit('scroll-to', this.number)
    }
  },
  computed: {
    ariaLabel () {
      return `Move to image ${ this.number }`
    }
  },
  template: `
    <button
      type="button"
      class="product-gallery-thumbnail"
      :aria-label="ariaLabel"
      @click="scrollTo"
      @keyup.enter="scrollTo"
    >
      <slot></slot>
    </button>
  `
})

const GalleryLightboxImage = Vue.component('gallery-lightbox-image', {
  props: {
    number: Number,
    imageSource: String
  },
  data: () => ({
    zoomInstance: null,
    defaultZoom: 2,
    maxZoom: 2,
    zoomEnabled: false,
    isMoving: false
  }),
  watch: {
    /**
     * Handle Zoom instance state on clicks.
     *
     * On click:
     * If the zoom is enabled, then enable, and zoom to `2`.
     * If zoom is disabled, then set zoom to `1`, and then disable.
     *
     * In the `initializeZoom` method, we also listen to the zoom instance
     * state, and toggle enabled if we zoom right out.
     */
    zoomEnabled (enabled) {
      console.log({ enabled, defaultZoom: this.defaultZoom })

      if (enabled) {
        this.zoomInstance?.setState({
          enable: true,
          currentZoom: this.defaultZoom
        })
      } else {
        this.zoomInstance?.setState({
          currentZoom: 1
        })
        this.zoomInstance?.setState({
          enable: false
        })
      }
    }
  },
  mounted () {
    this.initializeZoom()
  },
  methods: {
    /** Handler to toggle zoom to a preset amount, or back to the original, on click. */
    toggleZoom () {
      this.zoomEnabled = !this.zoomEnabled
    },
    /** Literally just to change the cursor to grab/grabbing based on state. */
    toggleMoving () {
      this.isMoving = !this.isMoving
    },
    /**
     * Handles initializing the zoom instance.
     */
    initializeZoom () {
      const containerEl = this.$refs.containerEl
      if (!containerEl) {
        console.warn('Image container element was not found - skipping initialising lightbox zoom.')
        return
      }

      this.zoomInstance = createZoomImageWheel(containerEl, {
        maxZoom: this.maxZoom,
        wheelZoomRatio: 0.2,
        initialState: {
          enable: false,
          currentZoom: 1
        }
      })

      this.zoomInstance.subscribe(({ state }) => {
        // If we zoom right out, disable zoom (as if we toggled off)
        if (state.currentZoom === 1) {
          this.zoomEnabled = false
        }
      })
    }
  },
  template: `
    <div
      role="button"
      tabindex="0"
      aria-label="Use enter or space to zoom in to this image."
      class="product-gallery-lightbox-image"
      :class="zoomEnabled ? isMoving ? 'cursor-grabbing' : 'cursor-grab' : 'cursor-zoom-in'"
      :data-image-number="number"
      ref="containerEl"
      @keyup.enter="toggleZoom"
      @keyup.space="toggleZoom"
      @pointerdown="toggleMoving"
      @pointerup="toggleMoving"
      v-click-or-long-press="{
        onClick: toggleZoom,
        delay: 200
      }"
    >
      <slot></slot>
    </div>
  `
})


const GalleryLightbox = Vue.component('gallery-lightbox', {
  components: {
    GalleryLightboxThumbnail,
    GalleryLightboxImage
  },
  props: {
    value: Boolean
  },
  watch: {
    value: {
      immediate: true,
      handler (active) {
        if (active) {
          document.body.style.overflow = 'hidden'
        } else {
          document.body.style.overflow = 'auto'
        }
      }
    }
  },
  methods: {
    toggleActive () {
      this.$emit('input', !this.value)
    },
    scrollTo (number) {
      this.$nextTick(() => {
        const imageEl = this.$refs.lightboxImagesEl.querySelector(`[data-image-number="${ number }"]`)
        if (imageEl) {
          imageEl.scrollIntoView({
            behavior: 'smooth',
          })
        }
      })
    }
  },
  template: `
    <div
      class="product-gallery-lightbox"
      :class="{'active': value }"
    >
      <button
        type="button"
        aria-label="Close lightbox"
        class="product-gallery-lightbox-close"
        @click="toggleActive"
        @keyup.enter="toggleActive"
      >
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="0" y1="20" x2="20" y2="0" stroke="#003159"/>
          <line x1="0" y1="0" x2="20" y2="20" stroke="#003159"/>
        </svg>
      </button>
      <div class="product-gallery-lightbox-thumbnails">
        <slot name="thumbnails" :scroll-to="scrollTo" />
      </div>
      <div class="product-gallery-lightbox-images" ref="lightboxImagesEl">
        <slot name="images"></slot>
      </div>
    </div>
  `
})

window.Components ??= {}
window.Components.GalleryLightbox = GalleryLightbox
